import { ERPPostingStatus } from 'API';
import { isUnconfirmedPostingError } from 'app/services/agave/erpUtils';
import { cdkSubscribe, isUnauthorizedSubscriptionError } from 'app/services/cdkRealtime';
import {
  OnUpdateHighnoteTransactionEventById,
  onUpdateHighnoteTransactionEventById,
} from 'graphql_custom/liveUpdates';
import { PostingContext, PostingSubscriptionCallbacks, Subscription } from './types';
import { AnyAction } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import { setTransactionPosting as setTransactionPostingAction } from 'app/pages/store/transactionsSlice';

const POSTING_TIMEOUT_MS = 90_000;

export interface PostingSubscriptionOptions {
  /**
   * Override the subscription's auto-cleanup timeout. Pass `null` to disable
   * it entirely — for a poll-based ERP (QBD-direct) the terminal status can
   * land minutes to hours later, so the default 90s "stuck" assumption is
   * wrong and would tear the subscription down (and surface a misleading
   * timeout error) long before the real completion arrives.
   */
  timeoutMs?: number | null;
  /**
   * Called exactly once, from inside this subscription's own cleanup() —
   * whether that fires from a terminal COMPLETED/FAILED update, an
   * unrecoverable subscription error, or the auto-timeout. A caller that
   * tracks this subscription externally (e.g. QBD's per-transaction dedup
   * registry) should use this to drop its own bookkeeping on every close
   * path, not just the ones it explicitly triggered itself by calling
   * `.unsubscribe()` — the poll-delivered terminal path never goes through
   * the caller's own unsubscribe call. (Yash review, PR #2529)
   */
  onClose?: () => void;
}

/**
 * Creates a subscription to listen for ERP posting status updates.
 * Handles PENDING, PROCESSING, COMPLETED, and FAILED states.
 * Includes a timeout to prevent the UI from getting permanently stuck
 * if the backend fails to send a terminal status update.
 */
export function createPostingSubscription(
  transactionId: string,
  context: PostingContext,
  callbacks: PostingSubscriptionCallbacks = {},
  options: PostingSubscriptionOptions = {}
): Subscription {
  const timeoutMs = options.timeoutMs === undefined ? POSTING_TIMEOUT_MS : options.timeoutMs;
  const {
    dispatch,
    enqueueSnackbar,
    t,
    setIsAgaveLoading,
    setTransactionPosting,
    applyLocalTransactionUpdates,
    mapAgaveErrorMessage,
    captureException,
  } = context;

  let hasReceivedUpdate = false;

  const cleanup = () => {
    clearTimeout(timeoutId);
    subscription.unsubscribe();
    options.onClose?.();
  };

  // SEE-1235: on the CDK API, which admits only the company selected in the token. Posting starts
  // from the selected company's transaction list, so the transaction's own company is that one.
  const subscription = cdkSubscribe<OnUpdateHighnoteTransactionEventById>(
    onUpdateHighnoteTransactionEventById,
    {
      id: transactionId,
      paidolId: context.transaction.paidolId,
    }
  ).subscribe({
    next: (event) => {
      const updatedTxn = event.data?.onUpdateHighnoteTransactionEventById;
      if (!updatedTxn) {
        // eslint-disable-next-line no-console
        console.warn('Subscription received update but no transaction data:', event);
        return;
      }

      const postingStatus = updatedTxn.erpPostingStatus;
      const postingError = updatedTxn.erpPostingError;

      // Handle PENDING/PROCESSING - save to Redux so status persists after navigation
      if (postingStatus === ERPPostingStatus.PENDING || postingStatus === ERPPostingStatus.PROCESSING) {
        dispatch(
          applyLocalTransactionUpdates([
            {
              id: transactionId,
              changes: {
                erpPostingStatus: updatedTxn.erpPostingStatus,
              },
            },
          ]) as any
        );
        if (postingStatus === ERPPostingStatus.PENDING && callbacks.onPending) {
          callbacks.onPending();
        }
        if (postingStatus === ERPPostingStatus.PROCESSING && callbacks.onProcessing) {
          callbacks.onProcessing();
        }
        return; // Keep listening, don't unsubscribe
      }

      if (hasReceivedUpdate) return;

      if (postingStatus === ERPPostingStatus.COMPLETED) {
        hasReceivedUpdate = true;

        // Trust the backend data - update Redux with exactly what backend sent
        dispatch(
          applyLocalTransactionUpdates([
            {
              id: transactionId,
              changes: {
                erpApprovalStatus: updatedTxn.erpApprovalStatus ?? false,
                agaveExpenseId: updatedTxn.agaveExpenseId ?? null,
                // SEE-1235: neither field is in the CDK payload. Both were null over Amplify too:
                // the posting writer's update mutation never selected them.
                agaveExpense: null,
                erpPostingStatus: updatedTxn.erpPostingStatus,
                erpPostingError: updatedTxn.erpPostingError ?? null,
                erpPostingWarning: null,
              },
            },
          ]) as any
        );

        if (callbacks.onCompleted) {
          callbacks.onCompleted(updatedTxn);
        } else {
          enqueueSnackbar(t('submitExpense.success'), { variant: 'success' });
        }

        setIsAgaveLoading(false);
        setTransactionPosting({ id: transactionId, isPosting: false });
        cleanup();
      } else if (postingStatus === ERPPostingStatus.FAILED) {
        hasReceivedUpdate = true;

        // SEE-1259: an UNCONFIRMED outcome must NOT be cleared back to "reviewed".
        // Doing so hides that an attempt happened and leaves Post as the obvious
        // next click — which is how a transaction the ERP had already accepted got
        // posted a second time. Keeping FAILED also matches what the backend
        // persisted, so the row no longer changes meaning on refresh.
        const isUnconfirmed = isUnconfirmedPostingError(postingError);

        // IMPORTANT: Batch both Redux updates together to ensure they apply atomically
        // This prevents race conditions where the UI might check state between updates
        batch(() => {
          // Clear posting flag so UI immediately stops showing "posting" state
          dispatch(setTransactionPostingAction({ id: transactionId, isPosting: false }));

          // A definitive rejection clears back to "reviewed" so the user can correct
          // the coding and post again immediately (unchanged behaviour).
          dispatch(
            applyLocalTransactionUpdates([
              {
                id: transactionId,
                changes: {
                  erpPostingStatus: isUnconfirmed ? ERPPostingStatus.FAILED : null,
                  erpPostingError: postingError, // Keep error message for reference
                  erpPostingWarning: null, // Backend clears it on FAILED too (SEE-1696)
                },
              },
            ]) as any
          );
        });

        setIsAgaveLoading(false);

        const errorMessage = postingError ? mapAgaveErrorMessage(postingError, t) : t('submitExpense.error');

        if (callbacks.onFailed) {
          callbacks.onFailed(errorMessage);
        } else {
          // SEE-1259: match the sidesheet banner's severity. A red toast saying
          // "we couldn't confirm this" followed by an amber banner reads as two
          // different verdicts on the same attempt.
          enqueueSnackbar(errorMessage, { variant: isUnconfirmed ? 'warning' : 'error' });
        }

        cleanup();
      }
    },
    error: (error: Error & { errors?: { message?: string }[] }) => {
      if (!hasReceivedUpdate && isUnauthorizedSubscriptionError(error)) {
        // SEE-1235: refused, e.g. the company was switched in another tab. The post may still be
        // running server-side, so never clear the status: that brings Post back, the duplicate-post
        // risk SEE-1259 guards against. For a direct-ERP company (QBD direct included),
        // usePendingErpPostingPoller then resolves the PENDING row; elsewhere it stays PENDING until a reload.
        hasReceivedUpdate = true;
        captureException(error);
        dispatch(
          applyLocalTransactionUpdates([
            { id: transactionId, changes: { erpPostingStatus: ERPPostingStatus.PENDING } },
          ]) as unknown as AnyAction
        );
        setIsAgaveLoading(false);
        setTransactionPosting({ id: transactionId, isPosting: false });
        cleanup();
        return;
      }
      if (!hasReceivedUpdate) {
        captureException(error);

        dispatch(
          applyLocalTransactionUpdates([
            {
              id: transactionId,
              changes: {
                erpPostingStatus: null,
                erpPostingError: error.message || t('submitExpense.error'),
              },
            },
          ]) as any
        );

        if (callbacks.onError) {
          callbacks.onError(error);
        } else {
          enqueueSnackbar(t('submitExpense.error'), { variant: 'error' });
        }
        setIsAgaveLoading(false);
        setTransactionPosting({ id: transactionId, isPosting: false });
        cleanup();
      } else {
        // eslint-disable-next-line no-console
        console.warn('Subscription error after completion:', error);
      }
    },
  });

  const timeoutId =
    timeoutMs === null
      ? undefined
      : setTimeout(() => {
          if (hasReceivedUpdate) return;
          hasReceivedUpdate = true;

          captureException(new Error(`ERP posting subscription timed out after ${timeoutMs}ms`));

          // Store error but do NOT clear posting state. Row stays locked so the user
          // cannot edit codes while the backend may still be processing. On refresh,
          // the actual backend status is fetched and the row reflects the real outcome.
          dispatch(
            applyLocalTransactionUpdates([
              {
                id: transactionId,
                changes: {
                  erpPostingError: t('submitExpense.timeout'),
                },
              },
            ]) as any
          );

          setIsAgaveLoading(false);
          enqueueSnackbar(t('submitExpense.timeout'), { variant: 'error' });
          cleanup();
        }, timeoutMs);

  return { unsubscribe: cleanup };
}
